import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './AppointmentManager.css';

function AppointmentManager() {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search/Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('week'); // today, week, month, all - default to week to show upcoming appointments
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, arrived, cancelled

  // Selected appointment for actions
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  // Reschedule state
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [rescheduleProvider, setRescheduleProvider] = useState('');
  const [providers, setProviders] = useState([]);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const today = new Date();
      let startDate, endDate;

      switch (dateFilter) {
        case 'today':
          startDate = today.toISOString().split('T')[0];
          endDate = startDate;
          break;
        case 'week':
          startDate = today.toISOString().split('T')[0];
          const weekEnd = new Date(today);
          weekEnd.setDate(weekEnd.getDate() + 7);
          endDate = weekEnd.toISOString().split('T')[0];
          break;
        case 'month':
          startDate = today.toISOString().split('T')[0];
          const monthEnd = new Date(today);
          monthEnd.setMonth(monthEnd.getMonth() + 1);
          endDate = monthEnd.toISOString().split('T')[0];
          break;
        case 'all':
        default:
          // Get appointments from 30 days ago to 60 days in future
          const pastDate = new Date(today);
          pastDate.setDate(pastDate.getDate() - 30);
          startDate = pastDate.toISOString().split('T')[0];
          const futureDate = new Date(today);
          futureDate.setDate(futureDate.getDate() + 60);
          endDate = futureDate.toISOString().split('T')[0];
          break;
      }

      const response = await api.get(`/appointments?startDate=${startDate}&endDate=${endDate}`);

      // Extract appointments from response (backend returns in response.data.data)
      const appointmentsData = response.data.data || response.data || [];

      // Sort appointments by date and time
      const sortedAppointments = appointmentsData.sort((a, b) => {
        const dateA = new Date(`${a.date || a.pc_eventDate} ${a.time || a.pc_startTime}`);
        const dateB = new Date(`${b.date || b.pc_eventDate} ${b.time || b.pc_startTime}`);
        return dateA - dateB;
      });

      setAppointments(sortedAppointments);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  const filterAppointments = useCallback(() => {
    let filtered = [...appointments];

    // Filter by search term (patient name, phone)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(apt => {
        const patientName = `${apt.patient?.firstName || ''} ${apt.patient?.lastName || ''}`.toLowerCase();
        const phone = apt.patient?.phoneNumber || '';
        return patientName.includes(search) || phone.includes(search);
      });
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(apt => {
        const status = apt.status || apt.pc_apptstatus || '-';
        switch (statusFilter) {
          case 'pending':
            return status === '-';
          case 'arrived':
            return status === '@';
          case 'cancelled':
            return status === '~';
          default:
            return true;
        }
      });
    }

    setFilteredAppointments(filtered);
  }, [appointments, searchTerm, statusFilter]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    filterAppointments();
  }, [filterAppointments]);

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const appointmentId = selectedAppointment.id || selectedAppointment.pc_eid;
      await api.delete(`/appointments/${appointmentId}`);

      setSuccess('Appointment cancelled successfully');
      setShowCancelConfirm(false);
      setSelectedAppointment(null);

      // Reload appointments
      await loadAppointments();
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setError('Failed to cancel appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleRescheduleClick = async (appointment) => {
    setSelectedAppointment(appointment);
    setShowRescheduleModal(true);
    setError('');
    setSuccess('');

    // Set initial reschedule date to current appointment date
    const currentDate = appointment.date || appointment.pc_eventDate;
    setRescheduleDate(currentDate.split('T')[0]);

    // Set current provider as default
    setRescheduleProvider(appointment.providerId || appointment.pc_aid || '');

    // Load providers
    await loadProviders();

    // Load available slots for the next 7 days
    await loadAvailableSlots(currentDate);
  };

  const loadProviders = async () => {
    try {
      const response = await api.get('/providers');
      const providersData = response.data.data || response.data || [];
      // Filter out admin and users without specialty
      const doctorProviders = providersData.filter(p => p.specialty && p.title);
      setProviders(doctorProviders);
    } catch (err) {
      console.error('Error loading providers:', err);
      setProviders([]);
    }
  };

  const loadAvailableSlots = async (fromDate) => {
    try {
      const startDate = fromDate.split('T')[0];
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);

      const response = await api.get(`/appointments/available?date=${startDate}`);
      setAvailableSlots(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error loading available slots:', err);
      setAvailableSlots([]);
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedAppointment || !rescheduleDate || !rescheduleTime) {
      setError('Please select both date and time');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const appointmentId = selectedAppointment.id || selectedAppointment.pc_eid;

      await api.put(`/appointments/${appointmentId}`, {
        date: rescheduleDate,
        time: rescheduleTime,
        providerId: rescheduleProvider,
        pc_eventDate: rescheduleDate,
        pc_startTime: rescheduleTime,
        pc_aid: rescheduleProvider
      });

      setSuccess('Appointment rescheduled successfully');
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      setRescheduleDate('');
      setRescheduleTime('');
      setRescheduleProvider('');

      // Reload appointments
      await loadAppointments();
    } catch (err) {
      console.error('Error rescheduling appointment:', err);
      setError('Failed to reschedule appointment');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case '@':
        return <span className="status-badge arrived">Arrived</span>;
      case '~':
        return <span className="status-badge cancelled">Cancelled</span>;
      case '-':
      default:
        return <span className="status-badge pending">Pending</span>;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '@': return '#28a745';
      case '~': return '#dc3545';
      case '-':
      default: return '#ffc107';
    }
  };

  return (
    <div className="appointment-manager">
      <div className="manager-header">
        <h2>Manage Appointments</h2>
        <button
          className="refresh-btn"
          onClick={loadAppointments}
          disabled={loading}
        >
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Patient name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Date Range:</label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="filter-select"
          >
            <option value="today">Today</option>
            <option value="week">Next 7 Days</option>
            <option value="month">Next 30 Days</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="arrived">Arrived</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Appointments List */}
      <div className="appointments-list">
        {loading ? (
          <div className="loading-spinner">Loading appointments...</div>
        ) : filteredAppointments.length === 0 ? (
          <div className="no-appointments">
            <p>No appointments found</p>
          </div>
        ) : (
          <div className="appointments-grid">
            {filteredAppointments.map((apt, index) => {
              const date = apt.date || apt.pc_eventDate;
              const time = apt.time || apt.pc_startTime;
              const status = apt.status || apt.pc_apptstatus || '-';
              const patientName = apt.patient
                ? `${apt.patient.firstName} ${apt.patient.lastName}`
                : `Patient ID: ${apt.patientId || apt.pc_pid}`;
              const reason = apt.reason || apt.pc_title || 'Office Visit';

              return (
                <div
                  key={apt.id || apt.pc_eid || index}
                  className="appointment-card"
                  style={{ borderLeftColor: getStatusColor(status) }}
                >
                  <div className="appointment-header">
                    <div className="appointment-date">
                      <div className="date">{formatDate(date)}</div>
                      <div className="time">{formatTime(time)}</div>
                    </div>
                    {getStatusBadge(status)}
                  </div>

                  <div className="appointment-body">
                    <div className="patient-info">
                      <strong>{patientName}</strong>
                      {apt.patient?.phoneNumber && (
                        <div className="phone">{apt.patient.phoneNumber}</div>
                      )}
                    </div>

                    {apt.provider && (
                      <div className="provider-info">
                        <span className="provider-label">👨‍⚕️ Provider:</span>{' '}
                        <span className="provider-name">
                          {apt.provider.title} {apt.provider.fullName || apt.provider.fname + ' ' + apt.provider.lname}
                          {apt.provider.specialty && ` - ${apt.provider.specialty}`}
                        </span>
                      </div>
                    )}

                    <div className="appointment-reason">{reason}</div>
                  </div>

                  {status !== '~' && (
                    <div className="appointment-actions">
                      <button
                        className="btn-secondary btn-small"
                        onClick={() => handleRescheduleClick(apt)}
                      >
                        📅 Reschedule
                      </button>
                      <button
                        className="btn-danger btn-small"
                        onClick={() => {
                          setSelectedAppointment(apt);
                          setShowCancelConfirm(true);
                        }}
                      >
                        ✕ Cancel
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="modal-overlay" onClick={() => setShowCancelConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Appointment?</h3>
            <p>Are you sure you want to cancel this appointment?</p>
            {selectedAppointment && (
              <div className="appointment-details">
                <p><strong>Patient:</strong> {
                  selectedAppointment.patient
                    ? `${selectedAppointment.patient.firstName} ${selectedAppointment.patient.lastName}`
                    : `Patient ID: ${selectedAppointment.patientId || selectedAppointment.pc_pid}`
                }</p>
                <p><strong>Date:</strong> {formatDate(selectedAppointment.date || selectedAppointment.pc_eventDate)}</p>
                <p><strong>Time:</strong> {formatTime(selectedAppointment.time || selectedAppointment.pc_startTime)}</p>
              </div>
            )}
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Appointment
              </button>
              <button
                className="btn-danger"
                onClick={handleCancelAppointment}
                disabled={loading}
              >
                {loading ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="modal-overlay" onClick={() => setShowRescheduleModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h3>Reschedule Appointment</h3>

            {selectedAppointment && (
              <div className="current-appointment-info">
                <h4>Current Appointment:</h4>
                <p><strong>Patient:</strong> {
                  selectedAppointment.patient
                    ? `${selectedAppointment.patient.firstName} ${selectedAppointment.patient.lastName}`
                    : `Patient ID: ${selectedAppointment.patientId || selectedAppointment.pc_pid}`
                }</p>
                <p><strong>Date:</strong> {formatDate(selectedAppointment.date || selectedAppointment.pc_eventDate)}</p>
                <p><strong>Time:</strong> {formatTime(selectedAppointment.time || selectedAppointment.pc_startTime)}</p>
              </div>
            )}

            <div className="reschedule-form">
              <div className="form-group">
                <label>New Date:</label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => {
                    setRescheduleDate(e.target.value);
                    loadAvailableSlots(e.target.value);
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Provider:</label>
                <select
                  value={rescheduleProvider}
                  onChange={(e) => setRescheduleProvider(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a provider (optional)</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.title} {provider.fullName} - {provider.specialty}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Available Time Slots:</label>
                {availableSlots.length > 0 ? (
                  <div className="time-slots-grid">
                    {availableSlots.slice(0, 12).map((slot, index) => (
                      <button
                        key={index}
                        className={`time-slot ${rescheduleTime === slot.time ? 'selected' : ''}`}
                        onClick={() => setRescheduleTime(slot.time)}
                      >
                        {formatTime(slot.time)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="no-slots">No available slots for selected date</p>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowRescheduleModal(false);
                  setRescheduleDate('');
                  setRescheduleTime('');
                  setRescheduleProvider('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleRescheduleSubmit}
                disabled={loading || !rescheduleDate || !rescheduleTime}
              >
                {loading ? 'Rescheduling...' : 'Reschedule Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppointmentManager;
